import { Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SharedDataService } from 'src/app/services/app/shared-service/shared-data.service';
import { SupportService } from 'src/app/services/core/support/support.service';
import { formatChatListTime } from 'src/app/utils/chat-time';
import { sweetAlert } from 'src/app/utils/swal';
import { getScopeName, isBrokerStaff, SUPPORT_PAGE_SIZES, trackById } from 'src/app/utils/support.utils';
@UntilDestroy()

@Component({
  selector: 'app-support-chat',
  templateUrl: './support-chat.component.html',
  styleUrls: ['./support-chat.component.css']
})
export class SupportChatComponent implements OnInit {
  chats: any = [];
  hasMoreChats = false;
  isLoadingChats = false;
  selectedChat: any;
  currentUser: any;
  isBrokerStaff = false;
  isContactPickerOpen = false;
  trackById = trackById;

  constructor(private supportService: SupportService, private sharedDataService: SharedDataService) { }

  ngOnInit() {
    this.getUser();
    this.getChats();
    this.listenChatEvents();
  }

  getUser() {
    this.sharedDataService.getUser().pipe(untilDestroyed(this)).subscribe((user) => {
      this.currentUser = user;
      this.isBrokerStaff = isBrokerStaff(user);
      this.chats.forEach((chat: any) => this.prepareChat(chat));
    });
  }

  // `before` loads the ones after the last loaded chat
  getChats(before?: string) {
    const queryParams: any = { limit: SUPPORT_PAGE_SIZES.chats };
    if (before) {
      queryParams.before = before;
    }

    this.isLoadingChats = true;
    this.supportService.getChats(queryParams).pipe(untilDestroyed(this)).subscribe((data: any) => {
      const chats = data.results.map((chat: any) => this.prepareChat(chat));
      this.chats = before ? [...this.chats, ...chats] : chats;
      this.hasMoreChats = data.totalPages > 1;
      this.isLoadingChats = false;
    }, (err) => {
      this.isLoadingChats = false;
      sweetAlert('Error', err, 'error', 'OK');
    });
  }

  onScrollChats(event: any) {
    const list = event.target;
    const isAtBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 50;
    if (isAtBottom && this.hasMoreChats && !this.isLoadingChats) {
      this.getChats(this.chats[this.chats.length - 1].lastMessageAt);
    }
  }

  listenChatEvents() {
    this.supportService.chatMessage$.pipe(untilDestroyed(this)).subscribe((data: any) => {
      if (data.chatType === 'direct') {
        this.onChatMessage(data.chatId, data.message);
      }
    });

    this.supportService.chatRead$.pipe(untilDestroyed(this)).subscribe((data: any) => {
      const chat = this.findChat(data.chatId);
      if (chat && data.userId === this.currentUser?.id) {
        chat.unreadCount = 0;
      }
    });
  }

  onChatMessage(chatId: string, message: any) {
    const chat = this.findChat(chatId);
    if (!chat) {
      // not loaded yet, e.g. started by someone else
      this.addChat(chatId);
      return;
    }

    chat.lastMessage = message.text || 'Attachment';
    chat.lastMessageAt = message.createdAt;
    this.prepareChat(chat);
    const isOwnMessage = message.sender?.id === this.currentUser?.id;
    if (!isOwnMessage && chat.id !== this.selectedChat?.id) {
      chat.unreadCount += 1;
    }
    this.chats = [chat, ...this.chats.filter((otherChat: any) => otherChat.id !== chatId)];
  }

  addChat(chatId: string) {
    this.supportService.getChat(chatId).pipe(untilDestroyed(this)).subscribe((chat: any) => {
      if (!this.findChat(chat.id)) {
        this.chats = [this.prepareChat(chat), ...this.chats];
      }
    });
  }

  findChat(chatId: string) {
    return this.chats.find((chat: any) => chat.id === chatId);
  }

  selectChat(chat: any) {
    this.selectedChat = chat;
    chat.unreadCount = 0;
  }

  // broker staff pick who to chat with, tenants always chat with the broker
  startNewChat() {
    if (this.isBrokerStaff) {
      this.isContactPickerOpen = true;
    } else {
      this.openDirectChat({});
    }
  }

  onSelectContact(contact: any) {
    if (!contact) {
      return;
    }
    this.isContactPickerOpen = false;
    this.openDirectChat({ scope: { [contact.type]: contact.id } });
  }

  openDirectChat(payload: any) {
    this.supportService.openDirectChat(payload).pipe(untilDestroyed(this)).subscribe((chat: any) => {
      const loadedChat = this.findChat(chat.id);
      if (loadedChat) {
        this.selectChat(loadedChat);
      } else {
        this.chats = [this.prepareChat(chat), ...this.chats];
        this.selectChat(chat);
      }
    }, (err) => {
      sweetAlert('Error', err, 'error', 'OK');
    });
  }

  // worked out once instead of on every change detection
  prepareChat(chat: any) {
    chat.title = this.isBrokerStaff ? getScopeName(chat) : 'Broker';
    chat.time = formatChatListTime(chat.lastMessageAt);
    return chat;
  }
}
